import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { appointmentBooksState, setAppointmentBooksState } from '../../../../../store/reducers/appointments';
import API from '../../../../../app/api';
import { setSnackbar } from '../../../../../store/reducers/globals';
import { useTranslation } from 'react-i18next';
import AgyleLoadingSpinner from '../../../../../components/other/agyleLoadingSpinner';
import Typography from '@mui/material/Typography';
import { useSessionStyles } from './useSessionStyles';
import clsx from 'clsx';
import { isEmpty, isUndefined } from 'lodash';
import { Box, Divider, Grid, Stack } from '@mui/material';
import AgyleSwitch from '../../../../../components/ui/switch/agyleSwitch';
import AppointmentSelectedAttendance from './components/appointmentSelectedAttendance';
import AppointmentAttendanceListItem from './components/appointmentAttendanceListItem';
import { APPOINTMENT_BOOK_TYPES, getAttendanceType } from '../../../../ward/map/patientList/utils/edReviewUtils';
import AppointmentTypeInfo from './components/appointmentTypeInfo';

const AttendanceList = ({ patient, activities, setActivities }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const classes = useSessionStyles();

    const [loadingActivity, setLoadingActivity] = useState(false);

    // Selector
    const bookState = useSelector(appointmentBooksState);

    useEffect(() => {
        if (patient) {
            setLoadingActivity(true);
            API.getPatientAttendancesHistory(patient.__id)
                .then((res) => {
                    const onlyEmergencyDepartmentActivities = res.filter((att) => att?.admission?.attendanceType === 'E');
                    setActivities(onlyEmergencyDepartmentActivities);
                })
                .catch((err) => {
                    console.error(err);
                    setActivities([]);
                    dispatch(setSnackbar({ type: 'error', message: t('Error loading patient activities history') }));
                })
                .finally(() => setLoadingActivity(false));
        } else {
            setActivities([]);
        }
    }, [patient]);

    useEffect(() => {
        if (patient?.activeAttendances?.length > 0 && isUndefined(bookState?.linkedAttendance) && activities) {
            const activeAttendance = patient?.activeAttendances?.find((e) => e.attendanceType === 'E');
            const activeActivity = activities?.find(
                (e) => e?.admission?.attendanceId === activeAttendance?.attendanceId && !e?.admission?.endDate,
            );

            let objToSave = {};
            if (activeActivity) {
                objToSave = {
                    linkToActivity: true,
                    linkedAttendance: activeActivity?.admission,
                    type: null,
                    // type: getAttendanceType(activeActivity)
                };

                dispatch(setSnackbar({ type: 'info', message: t('Linked to active activity') }));
            } else {
                objToSave = {
                    linkToActivity: false,
                    type: APPOINTMENT_BOOK_TYPES.NONE,
                };
                dispatch(setSnackbar({ type: 'warning', message: t('No active activity found') }));
            }
            dispatch(setAppointmentBooksState(objToSave));
        }
    }, [activities, patient?.activeAttendances, bookState?.linkedAttendance]);

    const handleCheckLinkedAttendance = (event) => {
        const { checked } = event.target;

        const toSave = {
            linkToActivity: checked,
            linkedAttendance: checked ? undefined : null,
        };
        if (!checked) toSave.type = APPOINTMENT_BOOK_TYPES.NONE;

        dispatch(setAppointmentBooksState(toSave));
    };

    return (
        <>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexGrow={1}>
                <Box sx={{ pl: 1.5, pb: 0.2 }}>
                    {!isEmpty(activities) && (
                        <AgyleSwitch
                            value={bookState.linkToActivity}
                            onChange={handleCheckLinkedAttendance}
                            label={t('Link to activity')}
                            disabled={!patient || loadingActivity || !activities || activities?.length === 0}
                        />
                    )}
                </Box>

                <Box>
                    <AppointmentTypeInfo activities={activities} />
                </Box>
            </Stack>

            <Grid item xs={12}>
                <AppointmentSelectedAttendance />
            </Grid>

            <Grid item xs={12}>
                <Divider sx={{ m: 1 }} />
            </Grid>

            <div className={clsx('scroll-normal-white', classes.attendanceListBox)}>
                {loadingActivity && (
                    <div style={{ height: 110 }}>
                        <AgyleLoadingSpinner label={t('Loading patient activities history')} />
                    </div>
                )}

                {!loadingActivity && activities && activities?.length > 0 && bookState.linkToActivity && (
                    <Grid container direction="column" style={{ gap: 5 }}>
                        {activities.map((att) => (
                            <Fragment key={att?.admission?.__id}>
                                <AppointmentAttendanceListItem attendance={att} />
                            </Fragment>
                        ))}
                    </Grid>
                )}

                {!loadingActivity && activities && activities?.length === 0 && (
                    <div>
                        <Typography variant="body1" color="textSecondary">
                            {t('No activities found')}
                        </Typography>
                    </div>
                )}
            </div>
        </>
    );
};

export default AttendanceList;
